import { v4 } from 'uuid';

import { Origin } from '@videoblocks/events-ts/lib/storyblocks/navigation/OriginEnum';
import { type Geo, PageViewEvent } from '@videoblocks/events-ts/lib/storyblocks/navigation/PageViewEvent';

import { constructUtmParamsForPageViewEvent, parseUtmParamsFromSearchParams } from '../UtmParams';
import { CommonRecordFactory } from '../shared/CommonRecordFactory';
import parseDevice from '../shared/parseDevice';
import searchIdentifiers from '../shared/searchIdentifiers';

export type PageViewEventFactoryOptions = {
    currentUrl: URL;
    headers: Headers;
    visitorCookieId: string | null;
    searchIdCookie: string | null;
    searchPageIdCookie: string | null;
    statusCode: number;
    referrer: URL | undefined;
    geo: Geo | null;
    sessionToken?: string | undefined;
    newLoginPageFeatureFlag: boolean;
};

async function create(options: PageViewEventFactoryOptions): Promise<PageViewEvent> {
    const {
        currentUrl,
        headers,
        sessionToken,
        visitorCookieId,
        searchIdCookie,
        searchPageIdCookie,
        statusCode,
        referrer,
        geo,
        newLoginPageFeatureFlag,
    } = options;

    const referrerUrl = referrer?.href;
    const ipAddress = headers.get('X-Forwarded-For') ?? '';
    const clickOrigin = currentUrl.searchParams.get('click-origin') ?? '';

    const event = new PageViewEvent();
    event.urls = { currentUrl: currentUrl.href, referrerUrl };
    event.meta = {
        uuid: v4(),
        emitted: Date.now(),
        origin: Origin.STORYBLOCKS_NEXTJS,
        trace: null, // Trace is set to null because the old trace code (StockBlocks) is for NewRelic - 2023-11-27 Clark
    };
    event.device = parseDevice(headers, referrerUrl);
    event.user = await CommonRecordFactory.user(sessionToken, visitorCookieId, newLoginPageFeatureFlag);
    event.geo = geo;
    event.searchIdentifiers = searchIdentifiers(searchIdCookie, searchPageIdCookie);
    event.statusCode = statusCode;
    event.ipAddress = ipAddress;
    event.clickOrigin = clickOrigin;

    const sanitizedUtmValues = parseUtmParamsFromSearchParams(currentUrl.searchParams ?? new URLSearchParams());
    event.utmParameters = constructUtmParamsForPageViewEvent(sanitizedUtmValues);

    return event;
}

export const PageViewEventFactory = { create };
